import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { formatCurrency } from '../../lib/formatters';
import { TrendingUp, AlertCircle, Zap, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE = {
  PARTNER: 'bg-purple-100 text-purple-800',
  INVESTOR_FEE: 'bg-blue-100 text-blue-800',
  INVESTOR: 'bg-green-100 text-green-800',
};

const DistributionSummaryCard = ({ deal, isAdmin, getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [form, setForm] = useState({ payment_amount: '', payment_reference: '' });

  const dealId = deal?.id;

  const fetchSummary = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/deals/${dealId}/distribution-summary`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
    } catch {
      // No distributions yet — keep null
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuthHeaders]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleDistribute = async () => {
    const amount = parseFloat(form.payment_amount);
    if (!amount || isNaN(amount) || amount === 0) {
      toast.error('Enter a valid non-zero payment amount');
      return;
    }
    setDistributing(true);
    try {
      await axios.post(
        `${API_URL}/deals/${dealId}/distribute`,
        { payment_amount: amount, payment_reference: form.payment_reference || undefined },
        { headers: getAuthHeaders() }
      );
      toast.success(`Distribution of ${formatCurrency(amount)} recorded`);
      setDistributeOpen(false);
      setForm({ payment_amount: '', payment_reference: '' });
      fetchSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Distribution failed');
    } finally {
      setDistributing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4" /> Distribution Summary</CardTitle></CardHeader>
        <CardContent><div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div></CardContent>
      </Card>
    );
  }

  // No syndications on this deal
  if (!data || !data.per_syndicator_breakdown || data.per_syndicator_breakdown.length === 0) {
    return null;
  }

  const rows = data.per_syndicator_breakdown;

  // Totals row
  const totals = rows.reduce(
    (acc, r) => ({
      gross: acc.gross + (r.total_gross_share || 0),
      fee: acc.fee + (r.total_collection_fee || 0),
      commission: acc.commission + (r.total_broker_commission || 0),
      credit: acc.credit + (r.total_origination_credit || 0),
      mgmt_fee: acc.mgmt_fee + (r.total_management_fee || 0),
      net: acc.net + (r.total_net || 0),
      rtr: acc.rtr + (r.remaining_rtr || 0),
    }),
    { gross: 0, fee: 0, commission: 0, credit: 0, mgmt_fee: 0, net: 0, rtr: 0 }
  );

  return (
    <>
      <Card data-testid="distribution-summary-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-purple-600" />
              Distribution Summary
              <Badge variant="outline" className="text-xs ml-1">{data.total_distribution_events} events</Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchSummary}
                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                data-testid="distribution-refresh-btn"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => setDistributeOpen(true)}
                  data-testid="manual-distribute-btn"
                >
                  <Zap className="h-3 w-3" />
                  Distribute Payment
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="distribution-table">
              <thead>
                <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2 text-left font-medium">Syndicator</th>
                  <th className="px-4 py-2 text-left font-medium">Role</th>
                  <th className="px-4 py-2 text-right font-medium">Part %</th>
                  <th className="px-4 py-2 text-right font-medium">Gross Share</th>
                  <th className="px-4 py-2 text-right font-medium">Coll. Fee</th>
                  <th className="px-4 py-2 text-right font-medium">Broker Comm.</th>
                  <th className="px-4 py-2 text-right font-medium">Orig. Credit</th>
                  <th className="px-4 py-2 text-right font-medium">Mgmt. Fee</th>
                  <th className="px-4 py-2 text-right font-medium">Net Amount</th>
                  <th className="px-4 py-2 text-right font-medium">Remaining RTR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.syndicator_id} className="hover:bg-gray-50" data-testid={`distribution-row-${r.syndicator_id}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{r.syndicator_name}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[r.role] || 'bg-gray-100 text-gray-700'}`}>
                        {r.role}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">{Number(r.ownership_percentage || 0).toFixed(1)}%</td>
                    <td className="px-4 py-2.5 text-right">{formatCurrency(r.total_gross_share)}</td>
                    <td className="px-4 py-2.5 text-right text-red-600">{r.total_collection_fee ? `(${formatCurrency(r.total_collection_fee)})` : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-red-600">{r.total_broker_commission ? `(${formatCurrency(r.total_broker_commission)})` : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-green-600">{r.total_origination_credit ? formatCurrency(r.total_origination_credit) : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-purple-600">{r.total_management_fee ? `(${formatCurrency(r.total_management_fee)})` : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{formatCurrency(r.total_net)}</td>
                    <td className="px-4 py-2.5 text-right text-orange-600">{formatCurrency(r.remaining_rtr)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-800">
                  <td className="px-4 py-2.5" colSpan={3}>Totals</td>
                  <td className="px-4 py-2.5 text-right">{formatCurrency(totals.gross)}</td>
                  <td className="px-4 py-2.5 text-right text-red-600">{totals.fee ? `(${formatCurrency(totals.fee)})` : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-red-600">{totals.commission ? `(${formatCurrency(totals.commission)})` : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-green-600">{totals.credit ? formatCurrency(totals.credit) : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-purple-600">{totals.mgmt_fee ? `(${formatCurrency(totals.mgmt_fee)})` : '—'}</td>
                  <td className="px-4 py-2.5 text-right">{formatCurrency(totals.net)}</td>
                  <td className="px-4 py-2.5 text-right text-orange-600">{formatCurrency(totals.rtr)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {data.deal_info?.collection_fee_rate != null && (
            <div className="px-4 py-2 border-t bg-gray-50 flex items-center gap-4 text-xs text-gray-500">
              <span>Collection fee rate: <strong>{data.deal_info.collection_fee_rate}%</strong></span>
              {data.deal_info.commission_rate != null && (
                <span>Broker commission rate: <strong>{data.deal_info.commission_rate}%</strong></span>
              )}
              {data.deal_info.balance != null && (
                <span>Deal balance: <strong className="text-orange-600">{formatCurrency(data.deal_info.balance)}</strong></span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Distribute Dialog */}
      <Dialog open={distributeOpen} onOpenChange={setDistributeOpen}>
        <DialogContent data-testid="distribute-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-purple-600" />
              Manual Distribution
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              This creates ledger entries immediately. Use this as a fallback when auto-distribution did not trigger.
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dist-amount">Payment Amount</Label>
              <Input
                id="dist-amount"
                type="number"
                step="0.01"
                placeholder="e.g. 1500.00"
                value={form.payment_amount}
                onChange={(e) => setForm(f => ({ ...f, payment_amount: e.target.value }))}
                data-testid="dist-amount-input"
              />
              <p className="text-xs text-gray-500">Use a negative value for reversals / clawbacks</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dist-ref">Payment Reference (optional)</Label>
              <Input
                id="dist-ref"
                placeholder="e.g. ACH-2024-0055"
                value={form.payment_reference}
                onChange={(e) => setForm(f => ({ ...f, payment_reference: e.target.value }))}
                data-testid="dist-ref-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistributeOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDistribute}
              disabled={distributing}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="distribute-confirm-btn"
            >
              {distributing ? 'Distributing...' : 'Distribute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DistributionSummaryCard;
